import { FC, useEffect, useMemo, useState } from 'react';
import { Dropdown, DropdownChangeEvent, globalToastService } from '@repo/atomic-components';
import { useLazyGetListOrgsQuery, useLazyPostSwitchOrgQuery } from '@/api';
import type { ListOrg } from '@/api/debug/listOrgs';
import { selectUserAboutData, useAppSelector } from '@/store';
import { themedDropdownPt } from '@/ui/components/themedDropdownPt';
import { GreenButton } from '@/ui/components/GreenButton';
import { DashboardSection } from './DashboardSection';

// Convert underscore-separated constant names to an all-caps display label —
// same treatment as the Header's org switcher so the two stay consistent.
const convertOrgNameToDisplay = (name: string): string => name.split('_').join(' ').toUpperCase();

/**
 * First dashboard section — shows the current organisation and lets a global
 * admin switch into another. Switching reloads the app (as the Header switcher
 * does) so every org-scoped query refetches against the newly-active org.
 */
interface SwitchOrgSectionProps {
  className?: string;
  /** Bumped by the parent after an org is created so the dropdown refetches. */
  refreshKey?: number;
}

export const SwitchOrgSection: FC<SwitchOrgSectionProps> = ({ className, refreshKey }) => {
  const userAboutData = useAppSelector(selectUserAboutData);

  const [triggerGetListOrgs, { isFetching: isLoadingOrgs }] = useLazyGetListOrgsQuery();
  const [triggerPostSwitchOrg, { isFetching: isSwitching }] = useLazyPostSwitchOrgQuery();

  const [orgs, setOrgs] = useState<ListOrg[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<ListOrg | null>(null);

  // Refetch on mount and whenever `refreshKey` changes (i.e. after a new org is
  // created) so the switcher list stays current without a manual page refresh.
  useEffect(() => {
    triggerGetListOrgs()
      .unwrap()
      .then((response) => {
        const sortedOrgs = [...response.orgs].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
        );
        setOrgs(sortedOrgs);
        if (userAboutData?.organisationId) {
          setSelectedOrg(sortedOrgs.find((o) => o.orgId === userAboutData.organisationId) ?? null);
        }
      })
      .catch((error) => console.error('Failed to fetch orgs:', error));
  }, [triggerGetListOrgs, userAboutData?.organisationId, refreshKey]);

  // The org currently active for this session, resolved from /user/about.
  const currentOrg = useMemo(
    () => orgs.find((o) => o.orgId === userAboutData?.organisationId) ?? null,
    [orgs, userAboutData?.organisationId],
  );

  const isDirty = !!selectedOrg && selectedOrg.orgId !== userAboutData?.organisationId;

  const handleSwitch = () => {
    if (!selectedOrg) return;
    triggerPostSwitchOrg({ orgId: selectedOrg.orgId })
      .unwrap()
      .then(() => {
        window.location.reload();
      })
      .catch((error) => {
        console.error('Failed to switch org:', error);
        globalToastService.show({ severity: 'error', summary: 'Error', detail: 'Failed to switch organisation.' });
      });
  };

  return (
    <DashboardSection
      title="Switch organisation"
      description="Select which organisation you are operating on. Switching reloads the app."
      className={className}
    >
      <div className="flex flex-col gap-1">
        <span className="text-fletchers-gray-200 text-sm">Current organisation</span>
        <span className="text-fletchers-black-900 text-[16px] font-semibold uppercase">
          {currentOrg ? convertOrgNameToDisplay(currentOrg.name) : '—'}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="switch-org" className="text-fletchers-gray-200 text-[14px]/[20px] font-[500]">
          Organisation
        </label>
        <Dropdown
          id="switch-org"
          value={selectedOrg}
          options={orgs}
          optionLabel="name"
          onChange={(e: DropdownChangeEvent) => setSelectedOrg(e.value as ListOrg)}
          placeholder={isLoadingOrgs ? 'Loading organisations…' : 'Select organisation'}
          className="w-full max-w-md"
          disabled={isLoadingOrgs}
          itemTemplate={(org: ListOrg) => <span className="uppercase">{convertOrgNameToDisplay(org.name)}</span>}
          valueTemplate={(org: ListOrg | null) =>
            org ? (
              <span className="text-fletchers-black-900 uppercase">{convertOrgNameToDisplay(org.name)}</span>
            ) : (
              <span className="text-fletchers-gray-200">
                {isLoadingOrgs ? 'Loading organisations…' : 'Select organisation'}
              </span>
            )
          }
          {...themedDropdownPt()}
        />
      </div>

      <div className="mt-auto flex justify-start pt-2">
        <GreenButton
          label="Switch organisation"
          icon="pi pi-sync"
          disabled={!isDirty || isSwitching}
          loading={isSwitching}
          onClick={handleSwitch}
          style={{ height: 36 }}
        />
      </div>
    </DashboardSection>
  );
};
